import {
  buildPatientClinicalTasksSearch,
  buildPatientClinicalTasksSubscriptionCriteria,
  compareClinicalTasks,
} from '@bariendo/lib/clinical-tasks';
import { Patient, Task } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSubscriptionWithLimitHandling } from '../services/subscriptions';

export interface UseClinicalTasksResult {
  /** Open clinical tasks for the patient, sorted overdue-first then by due date. */
  tasks: Task[];
  loading: boolean;
  /** Refetches the list; also invoked automatically by the live subscription. */
  refresh: () => Promise<void>;
}

/**
 * The patient Tasks tab's data source: open clinical tasks
 * (group-identifier `orbit-provider-task`) for one patient, kept live via a
 * Medplum subscription on the same population.
 *
 * On a failed refetch the last-known-good list is retained — clearing it
 * would render the affirmative "No tasks" summary off the back of a
 * transient error, which on a clinical surface reads as "nothing to do".
 */
export function useClinicalTasks(patient: Patient | undefined): UseClinicalTasksResult {
  const medplum = useMedplum();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchRequestIdRef = useRef(0);

  // `patient` comes from context state, so its identity is stable per patient
  // load; memoizing on it keeps the reference rules (createReference, never
  // string interpolation) without churning the search on every render.
  const searchParams = useMemo(() => (patient ? buildPatientClinicalTasksSearch(patient) : undefined), [patient]);

  const subscriptionCriteria = useMemo(
    () => (patient ? buildPatientClinicalTasksSubscriptionCriteria(patient) : undefined),
    [patient]
  );

  const refresh = useCallback(async () => {
    // Invalidate any in-flight response in EVERY path — including the early
    // return — otherwise a patient→undefined transition lets a stale
    // response for the previous patient repopulate the cleared list.
    const requestId = ++fetchRequestIdRef.current;

    if (!medplum.isAuthenticated() || !searchParams) {
      setTasks([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // cache: 'reload' — this refetch is subscription-driven, and Medplum's
      // 60s GET cache is only invalidated by LOCAL writes, so without it a
      // teammate's completion stays on screen for up to a minute.
      const results = await medplum.searchResources('Task', searchParams, { cache: 'reload' });
      if (requestId === fetchRequestIdRef.current) {
        const now = new Date();
        setTasks([...results].sort((a, b) => compareClinicalTasks(a, b, now)));
      }
    } catch {
      // Keep last-known-good; the next subscription tick or manual refresh retries.
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [medplum, searchParams]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useSubscriptionWithLimitHandling(subscriptionCriteria ?? '', refresh);

  return { tasks, loading, refresh };
}
